import Vue from 'vue';
import {CartItem, Gtmproduct, GtmBaseEvent, CustomerData, GtmSignUpEvent, GtmSignUpMethod} from '@/store/models';
import store from '@/store';
import {getCustomerSections} from '@/store/api';
import to from 'await-to-js';

async function getLocalStorageCustomer() {
    const mageCacheStorage = JSON.parse(localStorage.getItem('mage-cache-storage') as string);
    let customerData = mageCacheStorage.customer as CustomerData;

    // eslint-disable-next-line no-prototype-builtins
    if (!customerData || !customerData.hasOwnProperty('dataLayer')) {
        if (mageCacheStorage) {
            // get customer data of logged user from Magento
            const customerSectionsData = await to(getCustomerSections('customer'));
            const [, customerSectionsRes]: any = customerSectionsData;
            customerData = customerSectionsRes.customer;
            // update local storage
            mageCacheStorage.customer = customerData;
            localStorage.setItem('mage-cache-storage', JSON.stringify(mageCacheStorage));
            localStorage.setItem('ds-customer', JSON.stringify(customerData));
        }
    }

    return customerData;
}

/**
 * Push method abstraction
 *
 * @param dataLayer object
 */
function gtmPush(dataLayer: object) {
    if (Vue.gtm) {
        Vue.gtm.push(dataLayer);
    }
}

function getGtmBaseEvent(isUserLoggedIn: boolean): Omit<GtmBaseEvent, 'event'> {
    return {
        logged_in: isUserLoggedIn,
        site_lang: store.state.options.locale,
        traffic_type: 'standard',
    };
}

/**
 * DataLayer event `init`
 */
export async function eventInit(isUserLoggedIn: boolean) {
    const dataLayer: GtmBaseEvent = {
        ...getGtmBaseEvent(isUserLoggedIn),
        event: 'init',
    };

    if (isUserLoggedIn) {
        const customerData = await getLocalStorageCustomer();
        Object.assign(dataLayer, customerData.dataLayer);
    }

    gtmPush(dataLayer);
}

export function eventSignUp(method: GtmSignUpMethod, firstName: string, lastName: string, email: string) {
    const dataLayer: GtmSignUpEvent = {
        ...getGtmBaseEvent(true),
        event: 'sign_up',
        method,
        user_data: {
            email_address: email,
            address: {
                first_name: firstName,
                last_name: lastName,
            },
        },
    };

    gtmPush(dataLayer);
}

/**
 * Page View Tracking
 *
 * @param pagePath string
 */
const trackPageview = (pagePath: string) => {
    const dataLayer = {
        event: 'pageView',
        pagePath,
    };

    gtmPush(dataLayer);
};

/**
 * Convert cart item model to gtm product model
 *
 * @param products CartItem[]
 * @returns Gtmproduct[];
 */
const convertProductsData = (products: CartItem[]) => {
    return products.reduce<Gtmproduct[]>((acc, product) => {
        acc.push({
            name: product.name,
            id: product.sku,
            price: product.extension_attributes.extprice,
            brand: product.extension_attributes.extproduct_manufacturer,
            category: product.category,
            quantity: product.qty,
            attribute_set: product.extension_attributes.extproduct_attribute_set,
        });

        return acc;
    }, []);
};

/**
 * Track method selection
 *
 * @param step string
 * @param option string
 */
const trackMethod = (step: string, option: string) => {
    const dataLayer = {
        event: 'checkoutOption',
        ecommerce: {
            checkout_option: {
                actionField: {
                    step,
                    option,
                },
            },
        },
    };

    gtmPush(dataLayer);
};

/**
 * Track Checkout data
 *
 * @param products Gtmproduct[]
 * @param option string
 */
const trackCheckout = (products: Gtmproduct[], option: string, step: string) => {
    const dataLayer = {
        event: 'checkout',
        ecommerce: {
            currencyCode: 'EUR',
            checkout: {
                actionField: {step},
                products,
            },
        },
    };

    let dataLayerPageType;

    if (step === '1') {
        dataLayerPageType = {
            pageType: 'Cart',
        };
    } else {
        dataLayerPageType = {
            pageType: 'Checkout',
        };
    }

    gtmPush(dataLayerPageType);
    gtmPush(dataLayer);
};

/**
 * Track single product addition to cart
 *
 * @param products Gtmproduct[]
 */
const trackAdd = (products: Gtmproduct[]) => {
    const dataLayer = {
        event: 'addToCart',
        ecommerce: {
            currencyCode: 'EUR',
            add: {
                products,
            },
        },
    };

    gtmPush(dataLayer);
};

/**
 * Track single product removal from cart
 *
 * @param products Gtmproduct[]
 */
const trackRemove = (products: Gtmproduct[]) => {
    const dataLayer = {
        event: 'removeFromCart',
        ecommerce: {
            currencyCode: 'EUR',
            remove: {
                products,
            },
        },
    };

    gtmPush(dataLayer);
};

function addToWishlist(products: Gtmproduct[]) {
    const dataLayer = {
        event: 'addToWishlist',
        ecommerce: {
            addToWishlist: {
                products,
            },
        },
    };

    gtmPush(dataLayer);
}

export default {
    convertProductsData,
    gtmPush,
    trackAdd,
    trackCheckout,
    trackMethod,
    trackPageview,
    trackRemove,
    eventInit,
    addToWishlist,
};
